// RiskOverview.js - Goldman IC Memo Style Risk Overview Component
import React from "react";
import { dealsApi } from "../services/api";
import Card from "./ui/Card";
import ICSummary from "./ICSummary";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Info,
  FileText,
  Clock,
  Users,
  Shield,
  FileWarning,
} from "lucide-react";
import { computeCustomerExposure, getSeverityStyle, formatExposureDisplay, getMissingInputsDisplay } from "../lib/models/customerExposure";
import { selectBaseEbitda } from "../lib/models/ebitda";
import { formatPercent } from "../lib/math/percent";

// Extended tag set for multi-dimensional risk classification
const VALID_TAGS = {
  // Financial Dimension
  FINANCIAL_REPORTING: { color: '#1E40AF', bg: '#DBEAFE' },
  EARNINGS_QUALITY: { color: '#1E40AF', bg: '#DBEAFE' },
  LIQUIDITY: { color: '#1E40AF', bg: '#DBEAFE' },
  LEVERAGE: { color: '#1E40AF', bg: '#DBEAFE' },
  MARGIN_PRESSURE: { color: '#1E40AF', bg: '#DBEAFE' },
  
  // Revenue Dimension
  REVENUE_QUALITY: { color: '#059669', bg: '#D1FAE5' },
  CUSTOMER_CONCENTRATION: { color: '#059669', bg: '#D1FAE5' },
  REVENUE_VOLATILITY: { color: '#059669', bg: '#D1FAE5' },
  PRICING_RISK: { color: '#059669', bg: '#D1FAE5' },
  CONTRACT_RISK: { color: '#059669', bg: '#D1FAE5' },
  
  // Operational Dimension
  OPERATIONAL: { color: '#7C3AED', bg: '#EDE9FE' },
  SCALABILITY_RISK: { color: '#7C3AED', bg: '#EDE9FE' },
  IT_SYSTEMS: { color: '#7C3AED', bg: '#EDE9FE' },
  COST_STRUCTURE: { color: '#7C3AED', bg: '#EDE9FE' },
  CAPACITY_CONSTRAINT: { color: '#7C3AED', bg: '#EDE9FE' },
  
  // Legal & Compliance
  LEGAL: { color: '#DC2626', bg: '#FEE2E2' },
  REGULATORY: { color: '#DC2626', bg: '#FEE2E2' },
  LABOR_COMPLIANCE: { color: '#DC2626', bg: '#FEE2E2' },
  LITIGATION_RISK: { color: '#DC2626', bg: '#FEE2E2' },
  IP_RISK: { color: '#DC2626', bg: '#FEE2E2' },
  
  // Governance
  ACCOUNTING_CONTROLS: { color: '#EA580C', bg: '#FFEDD5' },
  GOVERNANCE: { color: '#EA580C', bg: '#FFEDD5' },
  AUDIT_QUALITY: { color: '#EA580C', bg: '#FFEDD5' },
  
  // Concentration Flags
  CONCENTRATION_RISK_HIGH: { color: '#991B1B', bg: '#FECACA' },
  EARNINGS_VARIABILITY_HIGH: { color: '#991B1B', bg: '#FECACA' },
  
  // Legacy tags for backward compatibility
  FINANCIAL: { color: '#1E40AF', bg: '#DBEAFE' },
  STRATEGIC: { color: '#059669', bg: '#D1FAE5' },
  COMPLIANCE: { color: '#DC2626', bg: '#FEE2E2' },
};

const getTagStyle = (tag) => {
  const upperTag = tag?.toUpperCase();
  return VALID_TAGS[upperTag] || { color: '#6B7280', bg: '#F3F4F6' };
};

// Inline styles for institutional memo look
const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  execSummaryCard: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  execSummaryHeader: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    paddingBottom: "12px",
    borderBottom: "1px solid #F3F4F6",
  },
  execSummaryTitle: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#374151",
    textTransform: "uppercase",
    letterSpacing: "0.025em",
    margin: 0,
  },
  execSummaryBody: {
    fontSize: "14px",
    lineHeight: 1.7,
    color: "#374151",
    margin: 0,
  },
  execSummaryParagraph: {
    marginBottom: "12px",
  },
  headerCard: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  headerRow: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "24px",
    flexWrap: "wrap",
  },
  headerLeft: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  headerRight: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: "12px",
  },
  title: {
    fontSize: "18px",
    fontWeight: 600,
    color: "#111827",
    margin: 0,
    letterSpacing: "-0.025em",
  },
  subtitle: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "14px",
    color: "#6B7280",
    margin: 0,
  },
  scoreContainer: {
    display: "flex",
    alignItems: "baseline",
    gap: "4px",
  },
  scoreValue: {
    fontSize: "36px",
    fontWeight: 700,
    color: "#111827",
    fontVariantNumeric: "tabular-nums",
  },
  scoreTotal: {
    fontSize: "18px",
    fontWeight: 500,
    color: "#9CA3AF",
  },
  confidenceBlock: {
    width: "192px",
  },
  confidenceLabel: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "12px",
    color: "#6B7280",
    marginBottom: "4px",
  },
  progressBar: {
    width: "100%",
    height: "6px",
    backgroundColor: "#F3F4F6",
    borderRadius: "3px",
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    backgroundColor: "#6B7280",
    borderRadius: "3px",
    transition: "width 0.3s ease",
  },
  timestamp: {
    fontSize: "12px",
    color: "#9CA3AF",
    margin: 0,
  },
  confidenceDrivers: {
    marginTop: "12px",
    padding: "10px 12px",
    backgroundColor: "#FEF3C7",
    borderRadius: "6px",
  },
  confidenceDriversLabel: {
    fontSize: "11px",
    fontWeight: 600,
    color: "#92400E",
    textTransform: "uppercase",
    letterSpacing: "0.3px",
    marginBottom: "6px",
    display: "block",
  },
  confidenceDriversList: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  confidenceDriverItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "4px 0",
    cursor: "help",
  },
  confidenceDriverLabel: {
    fontSize: "12px",
    color: "#92400E",
  },
  confidenceDriverImpact: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#92400E",
  },
  dataRequestSection: {
    marginTop: "12px",
  },
  dataRequestBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 14px",
    backgroundColor: "#EFF6FF",
    border: "1px solid #BFDBFE",
    borderRadius: "6px",
    fontSize: "12px",
    fontWeight: 600,
    color: "#1D4ED8",
    cursor: "pointer",
    transition: "all 0.15s ease",
  },
  sectionCard: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  sectionTitle: {
    fontSize: "16px",
    fontWeight: 600,
    color: "#111827",
    margin: 0,
    paddingBottom: "12px",
    borderBottom: "1px solid #F3F4F6",
  },
  driverRow: {
    display: "flex",
    gap: "16px",
    padding: "12px",
    borderRadius: "8px",
    transition: "background-color 0.2s ease",
  },
  driverRowHover: {
    backgroundColor: "#F9FAFB",
  },
  driverRank: {
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    backgroundColor: "#F3F4F6",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "12px",
    fontWeight: 600,
    color: "#6B7280",
    flexShrink: 0,
  },
  driverContent: {
    flex: 1,
    minWidth: 0,
  },
  driverHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "16px",
    marginBottom: "4px",
  },
  driverTitle: {
    fontSize: "14px",
    fontWeight: 500,
    color: "#111827",
    margin: 0,
  },
  driverImpact: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "12px",
    fontWeight: 500,
    color: "#6B7280",
    whiteSpace: "nowrap",
  },
  driverNarrative: {
    fontSize: "14px",
    color: "#6B7280",
    margin: "4px 0 0 0",
    lineHeight: 1.5,
  },
  driverTags: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
    marginTop: "8px",
  },
  linkedFindings: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginTop: "8px",
    fontSize: "12px",
    color: "#6B7280",
  },
  linkedFindingsLabel: {
    fontWeight: 500,
  },
  linkedFindingsTooltip: {
    cursor: "help",
    color: "#9CA3AF",
  },
  sectionSubtitle: {
    fontSize: "13px",
    color: "#6B7280",
    margin: "-8px 0 12px 0",
    fontStyle: "italic",
  },
  tag: {
    fontSize: "11px",
    padding: "2px 8px",
    borderRadius: "4px",
    backgroundColor: "#F3F4F6",
    color: "#6B7280",
    fontWeight: 500,
    textTransform: "uppercase",
  },

  matrixCommentary: {
    fontSize: "14px",
    color: "#6B7280",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  bucketsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "16px",
  },
  bucketCard: {
    display: "flex",
    flexDirection: "column",
  },
  bucketHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingBottom: "12px",
    borderBottom: "1px solid #F3F4F6",
    marginBottom: "12px",
  },
  bucketTitle: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  bucketTitleText: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#374151",
    textTransform: "uppercase",
    letterSpacing: "0.025em",
  },
  bucketCount: {
    fontSize: "13px",
    fontWeight: 600,
    padding: "2px 10px",
    borderRadius: "9999px",
  },
  bucketContent: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  bucketItem: {
    padding: "12px",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "background-color 0.2s ease",
  },
  bucketItemHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "8px",
    marginBottom: "4px",
  },
  bucketItemTitle: {
    fontSize: "14px",
    fontWeight: 500,
    color: "#111827",
    margin: 0,
  },
  bucketItemExposure: {
    fontSize: "11px",
    fontWeight: 500,
    whiteSpace: "nowrap",
  },
  bucketItemSummary: {
    fontSize: "13px",
    color: "#6B7280",
    margin: 0,
    lineHeight: 1.4,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  bucketItemTags: {
    display: "flex",
    flexWrap: "wrap",
    gap: "4px",
    marginTop: "8px",
  },
  bucketItemTag: {
    fontSize: "10px",
    padding: "2px 6px",
    borderRadius: "4px",
    fontWeight: 500,
    textTransform: "uppercase",
  },
  emptyState: {
    fontSize: "14px",
    color: "#9CA3AF",
    textAlign: "center",
    padding: "24px 0",
  },
  additionalItems: {
    fontSize: "12px",
    color: "#6B7280",
    textAlign: "center",
    padding: "8px 16px",
    backgroundColor: "#F3F4F6",
    border: "1px solid #E5E7EB",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: 500,
    transition: "all 0.2s ease",
  },
  overallBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "6px 14px",
    borderRadius: "9999px",
    fontSize: "13px",
    fontWeight: 600,
    border: "1px solid",
  },
  overallBadgeDot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
  },
  // Modal styles
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: "20px",
  },
  modalContent: {
    backgroundColor: "#FFFFFF",
    borderRadius: "12px",
    width: "100%",
    maxWidth: "600px",
    maxHeight: "80vh",
    overflow: "auto",
    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
  },
  modalHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "20px 24px",
    borderBottom: "1px solid #E5E7EB",
  },
  modalTitle: {
    fontSize: "18px",
    fontWeight: 600,
    color: "#111827",
    margin: 0,
    flex: 1,
    paddingRight: "16px",
  },
  modalClose: {
    background: "none",
    border: "none",
    fontSize: "20px",
    color: "#6B7280",
    cursor: "pointer",
    padding: "4px",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  modalBody: {
    padding: "24px",
  },
  modalBadgeRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "20px",
  },
  modalBadge: {
    padding: "6px 12px",
    borderRadius: "9999px",
    fontSize: "13px",
    fontWeight: 600,
    border: "1px solid",
  },
  modalExposure: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#DC2626",
  },
  modalSection: {
    marginBottom: "20px",
  },
  modalSectionTitle: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#6B7280",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    margin: "0 0 8px 0",
  },
  modalDescription: {
    fontSize: "15px",
    lineHeight: 1.6,
    color: "#374151",
    margin: 0,
  },
  modalDocument: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 14px",
    backgroundColor: "#F3F4F6",
    borderRadius: "8px",
    fontSize: "14px",
    color: "#374151",
    border: "1px solid #E5E7EB",
  },
  modalTags: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
  },
  modalTag: {
    fontSize: "11px",
    padding: "4px 10px",
    borderRadius: "6px",
    fontWeight: 600,
    textTransform: "uppercase",
  },
  addToFindingsButton: {
    width: "100%",
    padding: "12px 16px",
    borderRadius: "8px",
    border: "none",
    color: "#FFFFFF",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
};

// Severity color mappings
const severityColors = {
  red: {
    text: "#DC2626",
    bg: "#FEF2F2",
    border: "#FECACA",
    dot: "#DC2626",
    light: "#FEE2E2",
  },
  orange: {
    text: "#EA580C",
    bg: "#FFF7ED",
    border: "#FED7AA",
    dot: "#EA580C",
    light: "#FFEDD5",
  },
  low: {
    text: "#2563EB",
    bg: "#EFF6FF",
    border: "#BFDBFE",
    dot: "#2563EB",
    light: "#DBEAFE",
  },
  green: {
    text: "#059669",
    bg: "#ECFDF5",
    border: "#A7F3D0",
    dot: "#059669",
    light: "#D1FAE5",
  },
};

const getBucketIcon = (bucket) => {
  switch (bucket) {
    case "red":
      return <AlertCircle size={16} color={severityColors.red.text} />;
    case "orange":
      return <AlertTriangle size={16} color={severityColors.orange.text} />;
    case "low":
      return <Info size={16} color={severityColors.low.text} />;
    case "green":
      return <CheckCircle size={16} color={severityColors.green.text} />;
    default:
      return null;
  }
};

const formatDateTime = (isoString) => {
  if (!isoString) return "N/A";
  const date = new Date(isoString);
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Simple Progress Bar Component
const ProgressBar = ({ value }) => (
  <div style={styles.progressBar}>
    <div
      style={{
        ...styles.progressFill,
        width: `${Math.min(100, Math.max(0, value))}%`,
      }}
    />
  </div>
);

export const RiskOverview = ({ data, onClickItem, dealId }) => {
  // State for expanded buckets
  const [expandedBuckets, setExpandedBuckets] = React.useState({
    red: false,
    orange: false,
    low: false,
    green: false
  });

  // State for risk detail modal
  const [selectedRisk, setSelectedRisk] = React.useState(null);
  
  // State for add to findings feedback
  const [addedToFindings, setAddedToFindings] = React.useState(false);
  const [isAdding, setIsAdding] = React.useState(false);

  const toggleBucket = (bucket) => {
    setExpandedBuckets(prev => ({
      ...prev,
      [bucket]: !prev[bucket]
    }));
  };

  const handleRiskClick = (item) => {
    setSelectedRisk(item);
    setAddedToFindings(false);
  };

  const closeModal = () => {
    setSelectedRisk(null);
    setAddedToFindings(false);
  };
  
  // Map risk category/tags to findings notes category
  const mapToFindingCategory = (risk) => {
    const tags = risk.tags || [];
    const title = (risk.title || '').toLowerCase();
    
    // Check for financial-related tags/title
    if (tags.some(t => t.includes('FINANCIAL')) || 
        tags.some(t => t.includes('EARNINGS')) ||
        tags.some(t => t.includes('LIQUIDITY')) ||
        title.includes('financial') || title.includes('ebitda')) {
      return 'Finance, Accounting, Tax, QoE, NWC';
    }
    
    // Check for legal/compliance
    if (tags.some(t => t.includes('LEGAL')) || 
        tags.some(t => t.includes('REGULATORY')) ||
        tags.some(t => t.includes('CONTRACT')) ||
        tags.some(t => t.includes('COMPLIANCE')) ||
        title.includes('legal') || title.includes('contract')) {
      return 'Legal & Corporate';
    }
    
    // Check for customer/revenue
    if (tags.some(t => t.includes('CUSTOMER')) || 
        tags.some(t => t.includes('REVENUE')) ||
        title.includes('customer') || title.includes('revenue')) {
      return 'Clients & Revenue';
    }
    
    // Check for operational
    if (tags.some(t => t.includes('OPERATIONAL')) || 
        tags.some(t => t.includes('IT_SYSTEMS')) ||
        title.includes('operational') || title.includes('system')) {
      return 'Operations & Back Office';
    }
    
    // Default
    return 'Miscellaneous';
  };
  
  // Add risk to findings notes
  const handleAddToFindings = async () => {
    if (!dealId || !selectedRisk) return;
    
    setIsAdding(true);
    try {
      const newFinding = {
        date: new Date().toISOString().split('T')[0],
        category: mapToFindingCategory(selectedRisk),
        question: '',
        finding: `${selectedRisk.title}: ${selectedRisk.detail || selectedRisk.summary || ''}`,
        sourceDocument: selectedRisk.documentName || ''
      };

      await dealsApi.addNote(dealId, newFinding);
      setAddedToFindings(true);
      setTimeout(() => setAddedToFindings(false), 3000);
    } catch (error) {
      console.error('Error adding finding:', error);
      alert('Error adding to findings notes');
    } finally {
      setIsAdding(false);
    }
  };

  // Sort items by bucket
  const redItems = data.items
    ?.filter((i) => i.bucket === "red")
    .sort((a, b) => {
      if (b.severity !== a.severity) return b.severity - a.severity;
      return (b.exposure?.value || 0) - (a.exposure?.value || 0);
    }) || [];

  const orangeItems = data.items
    ?.filter((i) => i.bucket === "orange")
    .sort((a, b) => {
      if (b.severity !== a.severity) return b.severity - a.severity;
      return (b.exposure?.value || 0) - (a.exposure?.value || 0);
    }) || [];

  const lowItems = data.items
    ?.filter((i) => i.bucket === "low")
    .sort((a, b) => a.severity - b.severity) || [];

  const greenItems =
    data.items?.filter((i) => i.bucket === "green") || [];

  // New Prompt 10 format - Critical Risks (individual discrete issues) - MUST be defined first
  const criticalRisks = data.criticalRisks || [];
  
  // Build lookup for linked risk titles
  const criticalRiskMap = {};
  criticalRisks.forEach(r => {
    criticalRiskMap[r.id] = r;
  });
  
  // Fixed category order


  const getOverallSeverity = () => {
    switch (data.overall?.label) {
      case "HIGH RISK":
        return "red";
      case "STRUCTURED":
        return "orange";
      case "ACCEPTABLE":
        return "low";
      case "LOW RISK":
        return "green";
      default:
        return "low";
    }
  };

  const overallSev = getOverallSeverity();
  const overallColor = severityColors[overallSev];

  // Compute customer exposure using Base EBITDA
  const customerExposure = React.useMemo(() => {
    if (!data?.financials) return null;
    
    return computeCustomerExposure({
      financials: data.financials,
      largestCustomerPct: data.financials?.customerConcentration?.top1Percent ?? null,
      largestCustomerEvidence: data.financials?.customerConcentration?.evidence || [],
      hasMsa: data.financials?.contractClauses?.hasMsa ?? null,
      hasAssignmentClause: data.financials?.contractClauses?.hasAssignmentClause ?? null
    });
  }, [data?.financials]);

  return (
    <div style={styles.container}>
      {/* IC Summary Card (Prompt 12) */}
      <ICSummary data={data} dealName={data.documentName} />

      {/* Customer Concentration Risk Driver */}
      <CustomerConcentrationCard exposure={customerExposure} />


      {/* B) Flag Buckets */}
      <div style={styles.bucketsGrid}>
        {/* Red Flags */}
        <Card variant="outlined" padding="md">
          <div style={styles.bucketCard}>
            <div style={styles.bucketHeader}>
              <div style={styles.bucketTitle}>
                {getBucketIcon("red")}
                <span style={styles.bucketTitleText}>Critical Risk</span>
              </div>
              <span
                style={{
                  ...styles.bucketCount,
                  backgroundColor: severityColors.red.bg,
                  color: severityColors.red.text,
                }}
              >
                {data.counts?.red || 0}
              </span>
            </div>
            <div style={styles.bucketContent}>
              {redItems.length > 0 ? (
                <>
                  {(expandedBuckets.red ? redItems : redItems.slice(0, 2)).map((item) => (
                    <div
                      key={item.id}
                      onClick={() => handleRiskClick(item)}
                      style={{
                        ...styles.bucketItem,
                        backgroundColor: severityColors.red.bg,
                        border: `1px solid ${severityColors.red.border}`,
                        cursor: "pointer",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor =
                          severityColors.red.light;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor =
                          severityColors.red.bg;
                      }}
                    >
                      <div style={styles.bucketItemHeader}>
                        <h4 style={styles.bucketItemTitle}>{item.title}</h4>
                        {item.exposure && (
                          <span
                            style={{
                              ...styles.bucketItemExposure,
                              color: severityColors.red.text,
                            }}
                          >
                            {item.exposure.label}
                          </span>
                        )}
                      </div>
                      <p style={styles.bucketItemSummary}>{item.summary}</p>
                      {item.tags?.length > 0 && (
                        <div style={styles.bucketItemTags}>
                          {item.tags.map((tag) => {
                            const tagStyle = getTagStyle(tag);
                            return (
                              <span
                                key={tag}
                                style={{
                                  ...styles.bucketItemTag,
                                  backgroundColor: tagStyle.bg,
                                  color: tagStyle.color,
                                }}
                              >
                                {tag}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                  {redItems.length > 2 && (
                    <button
                      onClick={() => toggleBucket('red')}
                      style={styles.additionalItems}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#E5E7EB";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "#F3F4F6";
                      }}
                    >
                      {expandedBuckets.red ? "Show less" : `+${redItems.length - 2} additional items`}
                    </button>
                  )}
                </>
              ) : (
                <p style={styles.emptyState}>No critical risk items found</p>
              )}
            </div>
          </div>
        </Card>

        {/* Orange Flags */}
        <Card variant="outlined" padding="md">
          <div style={styles.bucketCard}>
            <div style={styles.bucketHeader}>
              <div style={styles.bucketTitle}>
                {getBucketIcon("orange")}
                <span style={styles.bucketTitleText}>Elevated Risk</span>
              </div>
              <span
                style={{
                  ...styles.bucketCount,
                  backgroundColor: severityColors.orange.bg,
                  color: severityColors.orange.text,
                }}
              >
                {data.counts?.orange || 0}
              </span>
            </div>
            <div style={styles.bucketContent}>
              {orangeItems.length > 0 ? (
                <>
                  {(expandedBuckets.orange ? orangeItems : orangeItems.slice(0, 2)).map((item) => (
                    <div
                      key={item.id}
                      onClick={() => handleRiskClick(item)}
                      style={{
                        ...styles.bucketItem,
                        backgroundColor: severityColors.orange.bg,
                        border: `1px solid ${severityColors.orange.border}`,
                        cursor: "pointer",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor =
                          severityColors.orange.light;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor =
                          severityColors.orange.bg;
                      }}
                    >
                      <div style={styles.bucketItemHeader}>
                        <h4 style={styles.bucketItemTitle}>{item.title}</h4>
                        {item.exposure && (
                          <span
                            style={{
                              ...styles.bucketItemExposure,
                              color: severityColors.orange.text,
                            }}
                          >
                            {item.exposure.label}
                          </span>
                        )}
                      </div>
                      <p style={styles.bucketItemSummary}>{item.summary}</p>
                      {item.tags?.length > 0 && (
                        <div style={styles.bucketItemTags}>
                          {item.tags.map((tag) => {
                            const tagStyle = getTagStyle(tag);
                            return (
                              <span
                                key={tag}
                                style={{
                                  ...styles.bucketItemTag,
                                  backgroundColor: tagStyle.bg,
                                  color: tagStyle.color,
                                }}
                              >
                                {tag}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                  {orangeItems.length > 2 && (
                    <button
                      onClick={() => toggleBucket('orange')}
                      style={styles.additionalItems}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#E5E7EB";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "#F3F4F6";
                      }}
                    >
                      {expandedBuckets.orange ? "Show less" : `+${orangeItems.length - 2} additional items`}
                    </button>
                  )}
                </>
              ) : (
                <p style={styles.emptyState}>No elevated risk items found</p>
              )}
            </div>
          </div>
        </Card>

        {/* Low Risk */}
        <Card variant="outlined" padding="md">
          <div style={styles.bucketCard}>
            <div style={styles.bucketHeader}>
              <div style={styles.bucketTitle}>
                {getBucketIcon("low")}
                <span style={styles.bucketTitleText}>Low Risk</span>
              </div>
              <span
                style={{
                  ...styles.bucketCount,
                  backgroundColor: severityColors.low.bg,
                  color: severityColors.low.text,
                }}
              >
                {data.counts?.low || 0}
              </span>
            </div>
            <div style={styles.bucketContent}>
              {lowItems.length > 0 ? (
                <>
                  {lowItems.map((item) => (
                    <div
                      key={item.id}
                      onClick={() => handleRiskClick(item)}
                      style={{
                        ...styles.bucketItem,
                        backgroundColor: severityColors.low.bg,
                        border: `1px solid ${severityColors.low.border}`,
                        cursor: "pointer",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor =
                          severityColors.low.light;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor =
                          severityColors.low.bg;
                      }}
                    >
                      <h4 style={styles.bucketItemTitle}>{item.title}</h4>
                      <p style={styles.bucketItemSummary}>{item.summary}</p>
                      {item.tags?.length > 0 && (
                        <div style={styles.bucketItemTags}>
                          {item.tags.map((tag) => {
                            const tagStyle = getTagStyle(tag);
                            return (
                              <span
                                key={tag}
                                style={{
                                  ...styles.bucketItemTag,
                                  backgroundColor: tagStyle.bg,
                                  color: tagStyle.color,
                                }}
                              >
                                {tag}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </>
              ) : (
                <p style={styles.emptyState}>No low risk items found</p>
              )}
            </div>
          </div>
        </Card>

        {/* Green Flags */}
        <Card variant="outlined" padding="md">
          <div style={styles.bucketCard}>
            <div style={styles.bucketHeader}>
              <div style={styles.bucketTitle}>
                {getBucketIcon("green")}
                <span style={styles.bucketTitleText}>Positive Indicators</span>
              </div>
              <span
                style={{
                  ...styles.bucketCount,
                  backgroundColor: severityColors.green.bg,
                  color: severityColors.green.text,
                }}
              >
                {data.counts?.green || 0}
              </span>
            </div>
            <div style={styles.bucketContent}>
              {greenItems.length > 0 ? (
                <>
                  {(expandedBuckets.green ? greenItems : greenItems.slice(0, 3)).map((item) => (
                    <div
                      key={item.id}
                      onClick={() => handleRiskClick(item)}
                      style={{
                        ...styles.bucketItem,
                        backgroundColor: severityColors.green.bg,
                        border: `1px solid ${severityColors.green.border}`,
                        cursor: "pointer",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor =
                          severityColors.green.light;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor =
                          severityColors.green.bg;
                      }}
                    >
                      <div style={styles.bucketItemHeader}>
                        <h4 style={styles.bucketItemTitle}>{item.title}</h4>
                        {item.exposure && (
                          <span
                            style={{
                              ...styles.bucketItemExposure,
                              color: severityColors.green.text,
                            }}
                          >
                            {item.exposure.label}
                          </span>
                        )}
                      </div>
                      <p style={styles.bucketItemSummary}>{item.summary}</p>
                      {item.tags?.length > 0 && (
                        <div style={styles.bucketItemTags}>
                          {item.tags.map((tag) => {
                            const tagStyle = getTagStyle(tag);
                            return (
                              <span
                                key={tag}
                                style={{
                                  ...styles.bucketItemTag,
                                  backgroundColor: tagStyle.bg,
                                  color: tagStyle.color,
                                }}
                              >
                                {tag}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                  {greenItems.length > 3 && (
                    <button
                      onClick={() => toggleBucket('green')}
                      style={styles.additionalItems}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#E5E7EB";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "#F3F4F6";
                      }}
                    >
                      {expandedBuckets.green ? "Show less" : `+${greenItems.length - 3} additional items`}
                    </button>
                  )}
                </>
              ) : (
                <p style={styles.emptyState}>No positive indicators found</p>
              )}
            </div>
          </div>
        </Card>
      </div>

      {/* Risk Detail Modal */}
      {selectedRisk && (
        <div 
          style={styles.modalOverlay}
          onClick={closeModal}
        >
          <div 
            style={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>{selectedRisk.title}</h3>
              <button 
                onClick={closeModal}
                style={styles.modalClose}
              >
                ✕
              </button>
            </div>
            
            <div style={styles.modalBody}>
              {/* Risk Level Badge */}
              <div style={styles.modalBadgeRow}>
                <span 
                  style={{
                    ...styles.modalBadge,
                    backgroundColor: severityColors[selectedRisk.bucket]?.bg || '#F3F4F6',
                    color: severityColors[selectedRisk.bucket]?.text || '#374151',
                    borderColor: severityColors[selectedRisk.bucket]?.border || '#E5E7EB',
                  }}
                >
                  {selectedRisk.bucket === 'red' && 'Critical Risk'}
                  {selectedRisk.bucket === 'orange' && 'Elevated Risk'}
                  {selectedRisk.bucket === 'low' && 'Low Risk'}
                  {selectedRisk.bucket === 'green' && 'Positive Indicator'}
                </span>
                {selectedRisk.exposure && (
                  <span style={styles.modalExposure}>
                    {selectedRisk.exposure.label}
                  </span>
                )}
              </div>

              {/* Full Description */}
              <div style={styles.modalSection}>
                <h4 style={styles.modalSectionTitle}>Description</h4>
                <p style={styles.modalDescription}>
                  {selectedRisk.detail || selectedRisk.summary}
                </p>
              </div>

              {/* Source Document */}
              {selectedRisk.documentName && (
                <div style={styles.modalSection}>
                  <h4 style={styles.modalSectionTitle}>Source Document</h4>
                  <div style={styles.modalDocument}>
                    <FileText size={16} />
                    <span>{selectedRisk.documentName}</span>
                  </div>
                </div>
              )}

              {/* Tags */}
              {selectedRisk.tags?.length > 0 && (
                <div style={styles.modalSection}>
                  <h4 style={styles.modalSectionTitle}>Risk Categories</h4>
                  <div style={styles.modalTags}>
                    {selectedRisk.tags.map((tag) => {
                      const tagStyle = getTagStyle(tag);
                      return (
                        <span
                          key={tag}
                          style={{
                            ...styles.modalTag,
                            backgroundColor: tagStyle.bg,
                            color: tagStyle.color,
                          }}
                        >
                          {tag}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Add to Findings Button */}
              {dealId && (
                <div style={styles.modalSection}>
                  <button
                    onClick={handleAddToFindings}
                    disabled={isAdding || addedToFindings}
                    style={{
                      ...styles.addToFindingsButton,
                      backgroundColor: addedToFindings ? '#059669' : '#161f4a',
                      opacity: isAdding ? 0.7 : 1,
                    }}
                  >
                    {isAdding ? 'Adding...' : addedToFindings ? '✓ Added to Findings' : '+ Add to Findings Notes'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Customer Concentration Risk Driver Card
 * Displays EBITDA at risk from largest customer concentration
 */
function CustomerConcentrationCard({ exposure }) {
  if (!exposure) {
    return (
      <Card variant="outlined" padding="lg">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <Users size={20} color="#6B7280" />
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#111827', margin: 0 }}>
            Customer Concentration
          </h3>
        </div>
        <p style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>
          Not available - financial data not loaded
        </p>
      </Card>
    );
  }

  const display = formatExposureDisplay(exposure);
  const severityStyle = getSeverityStyle(exposure.severity);

  return (
    <Card variant="outlined" padding="lg">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Users size={20} color="#059669" />
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#111827', margin: 0 }}>
            Customer Concentration
          </h3>
        </div>
        {exposure.canCompute && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '12px',
              fontWeight: 600,
              padding: '4px 12px',
              borderRadius: '9999px',
              backgroundColor: severityStyle.bg,
              color: severityStyle.color,
              border: `1px solid ${severityStyle.border}`,
            }}
          >
            {exposure.severity}
          </span>
        )}
      </div>

      {!exposure.canCompute ? (
        <div>
          <p style={{ fontSize: '14px', color: '#92400E', margin: '0 0 12px 0', fontStyle: 'italic' }}>
            Cannot compute exposure - missing required inputs
          </p>
          <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#78350F' }}>
            {getMissingInputsDisplay(exposure.missingInputs).map((input, idx) => (
              <li key={idx}>{input}</li>
            ))}
          </ul>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {/* Key Metrics Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
            <div>
              <p style={{ fontSize: '11px', color: '#6B7280', margin: '0 0 4px 0', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                Largest Customer
              </p>
              <p style={{ fontSize: '18px', fontWeight: 600, color: '#111827', margin: 0, fontFamily: 'SF Mono, monospace' }}>
                {display.largestCustomerPct}
                <span style={{ fontSize: '11px', color: '#6B7280', marginLeft: '4px', fontFamily: 'system-ui' }}>of revenue</span>
              </p>
            </div>
            <div>
              <p style={{ fontSize: '11px', color: '#6B7280', margin: '0 0 4px 0', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                EBITDA Margin (Base)
              </p>
              <p style={{ fontSize: '18px', fontWeight: 600, color: '#111827', margin: 0, fontFamily: 'SF Mono, monospace' }}>
                {display.ebitdaMargin}
              </p>
              <p style={{ fontSize: '11px', color: '#9CA3AF', margin: '2px 0 0 0' }}>
                {exposure.calculated.ebitdaMargin.formula}
              </p>
            </div>
            <div>
              <p style={{ fontSize: '11px', color: '#6B7280', margin: '0 0 4px 0', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                EBITDA at Risk
              </p>
              <p style={{ fontSize: '18px', fontWeight: 600, color: exposure.severity === 'Severe' ? '#DC2626' : exposure.severity === 'Elevated' ? '#92400E' : '#059669', margin: 0, fontFamily: 'SF Mono, monospace' }}>
                {display.exposureDollar}
              </p>
              <p style={{ fontSize: '11px', color: '#9CA3AF', margin: '2px 0 0 0' }}>
                {display.exposurePctOfEbitda} of Base EBITDA
              </p>
            </div>
          </div>

          {/* Clause Badges */}
          {(exposure.clauseEscalations.noMsa || exposure.clauseEscalations.noAssignment) && (
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              {exposure.clauseEscalations.noMsa && (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '11px',
                    fontWeight: 600,
                    padding: '4px 10px',
                    borderRadius: '4px',
                    backgroundColor: '#FEE2E2',
                    color: '#991B1B',
                    border: '1px solid #FCA5A5',
                  }}
                  title="No Master Service Agreement - increases risk"
                >
                  <FileWarning size={12} />
                  No MSA
                </span>
              )}
              {exposure.clauseEscalations.noAssignment && (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '11px',
                    fontWeight: 600,
                    padding: '4px 10px',
                    borderRadius: '4px',
                    backgroundColor: '#FEE2E2',
                    color: '#991B1B',
                    border: '1px solid #FCA5A5',
                  }}
                  title="No assignment clause - increases risk"
                >
                  <Shield size={12} />
                  No assignment clause
                </span>
              )}
            </div>
          )}

          {/* Evidence Reference */}
          {exposure.evidence?.largestCustomerPct?.length > 0 && (
            <div style={{ marginTop: '8px', paddingTop: '12px', borderTop: '1px solid #E5E7EB' }}>
              <p style={{ fontSize: '11px', color: '#6B7280', margin: '0 0 6px 0' }}>
                Source: {exposure.evidence.largestCustomerPct[0].docName}
                {exposure.evidence.largestCustomerPct[0].page && ` p.${exposure.evidence.largestCustomerPct[0].page}`}
              </p>
              {exposure.evidence.largestCustomerPct[0].snippet && (
                <p style={{ fontSize: '12px', color: '#4B5563', margin: 0, fontStyle: 'italic' }}>
                  "{exposure.evidence.largestCustomerPct[0].snippet.substring(0, 100)}..."
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

export default RiskOverview;
